import { afterNextRender, Component, computed, ElementRef, HostListener, inject, OnInit, Signal, signal, WritableSignal } from '@angular/core';
import { environment } from '../../environments/environment';
import { DeliveriesService } from '../settings/account/deliveries/deliveries.service';
import { DeliveryInformation } from '../settings/account/deliveries/intarfaces';
import { CalculationsService } from '../shared/calculations.service';
import { ModalContentTypes } from '../shared/constants/modal-content-types';
import { ModalContentService } from '../shared/modal-content/modal-content.service';
import { OrderService } from '../shared/order.service';
import { ResolutionService } from '../shared/resolution.service';
import { SignalsStoreService } from '../shared/signals-store.service';
import { TipsDonationsService } from '../shared/tips-donations.service';
import { RELATED_ORDER_PRODUCTS, Fee } from '../shared/types/order.type';
import { formatDateToReadableString } from '../shared/utils/formatting';
import { Session } from '../shared/types/session.type';
import { Router, RouterLink } from '@angular/router';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { CommonModule } from '@angular/common';
import { ProductCartPreview } from '../shared/product-card/product-cart-preview/product-cart-preview.component';
import { isANoDeliveryDayUser, isANoPaymentMethodUser, isNoAddressUser, trackBeginCheckoutEvent } from '../shared/common/utils';
import { DataLayerService } from '../shared/data-layer/data-layer.service';
import { LocalStorageService } from '../shared/local-storage.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { finalize, tap } from 'rxjs';
import { updateFirebaseOrderAndSubTrigger } from '../shared/common/order.utils';
import { FirebaseCrudService } from '../shared/firebase-crud.service';
import { CarouselComponent } from '../shared/carousel/carousel.component';
import { EmptyMessageComponent } from '../shared/empty-message/empty-message.component';
import { FormsModule } from '@angular/forms';
import { LOCALSTORAGE_KEYS } from '../shared/constants/databases';
import { SubscriptionsService } from '../settings/account/subscriptions/subscriptions.service';
import { MembershipService } from '../settings/billing/membership/membership.service';
import { MembershipSavingsComponent } from '../shared/membership-savings/membership-savings.component';
import { MINIMUM_SPEND_VALIDATION_METHOD, MinimumSpendValidationMethod } from '../shared/constants/minimum-spend';
import { PastDueBannerComponent } from '../shared/past-due-banner/past-due-banner.component';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'app-side-checkout',
  providers: [MembershipService],
  imports: [
    PastDueBannerComponent,
    RouterLink,
    MatProgressBarModule,
    CommonModule,
    ProductCartPreview,
    CarouselComponent,
    EmptyMessageComponent,
    FormsModule,
    MembershipSavingsComponent,
    NgbTooltipModule,
  ],
  templateUrl: './side-checkout.component.html',
  styleUrl: './side-checkout.component.scss'
})
export class SideCheckoutComponent implements OnInit {
  signalsStoreService = inject(SignalsStoreService);
  private orderService = inject(OrderService);
  private resolutionService = inject(ResolutionService);
  private deliveriesService = inject(DeliveriesService);
  #calculationsService = inject(CalculationsService);
  #eRef = inject(ElementRef);
  #router = inject(Router);
  #modalContentService = inject(ModalContentService);
  #tipsDonationsService = inject(TipsDonationsService);
  #localStorageService = inject(LocalStorageService);
  #activeModal = inject(NgbModal);
  #firebaseCrudService = inject(FirebaseCrudService);
  #subscriptionsService = inject(SubscriptionsService);
  #dataLayerService = inject(DataLayerService);
  #membershipService = inject(MembershipService);

  groupedProducts: Signal<any> = computed(() => this.groupProducts());
  odooOrderProducts: Signal<any> = computed(() => this.setUpOdooProducts());
  showCartPreview = this.signalsStoreService.showCartPreview;

  isContentLoaded: Signal<any> = computed(() => this.signalsStoreService.isContentLoaded());
  odooOrder: Signal<any | null> = computed(() => this.orderService.odooOrder());
  products: Signal<any | null> = computed(() => this.setUpProducts());
  firebaseOrder: Signal<any | null> = computed(() => this.signalsStoreService.firebaseOrder());
  isMobile = computed(() => this.resolutionService.isMobile());

  carouselBuyAgainProducts: Signal<any | undefined> = computed(() => this.setUpCarouselItems(RELATED_ORDER_PRODUCTS.BUY_AGAIN, false));
  carouselFavoritesProducts: Signal<any | undefined> = computed(() => this.setUpCarouselItems(RELATED_ORDER_PRODUCTS.FAVORITES, false));
  carouselSuggestedProducts: Signal<any | undefined> = computed(() => this.setUpCarouselItems(RELATED_ORDER_PRODUCTS.SUGGESTED));

  //#region Properties

  showFreeDeliverySlider = signal(environment.config.flows.order.showFreeDeliverySlider);

  hasVoluntaryDonationFlow = signal(environment.config.flows.order.voluntaryDonationFlow);

  hasDriverTipsFlow = signal(environment.config.flows.order.hasDriveTipsFlow);

  deliveryInfo = computed(() => this.#setUpDeliveryInfo());

  orderSubTotal = computed(() => this.#calculationsService.getOrderSubTotal(this.odooOrder(), this.firebaseOrder()) || 0);

  orderOriginalPriceSubtotal: Signal<number> = computed(() => this.#getOriginalPriceSubtotal());

  minimumSpendValidationMethod: Signal<MinimumSpendValidationMethod> = computed(
    // Odoo always sends this key in production. The fallback mirrors the legacy
    // behaviour DEV-8732 preserves for existing installations, so a missing key
    // can never make the cart contradict the backend's own minimum check.
    () => this.odooOrder()?.paymentDetails?.minimumSpendValidationMethod || MINIMUM_SPEND_VALIDATION_METHOD.before_discounts
  );

  // Subtotal every spend threshold on this order is measured against: the order
  // minimum, the free-delivery threshold and the delivery-fee range alike. Mirrors
  // the backend rule set by `sales_smc.minimum_spend_validation_method` so the UI
  // never disagrees with what the backend charges.
  // Both sides are the client-side subtotals on purpose: they track cart edits that
  // have not been saved yet, which the backend fields cannot.
  qualifyingSubtotal: Signal<number> = computed(() =>
    this.minimumSpendValidationMethod() === MINIMUM_SPEND_VALIDATION_METHOD.before_discounts
      ? this.orderOriginalPriceSubtotal()
      : this.orderSubTotal()
  );

  // True when the shopper is on a discounted pricelist: the subtotal the minimum
  // is measured against sits above what they actually pay.
  hasPricelistDiscount: Signal<boolean> = computed(() => this.orderOriginalPriceSubtotal() > this.orderSubTotal());

  // Explain the pre-discount basis only when the minimum is validated on public
  // prices while the shopper looks at a discounted cart. Otherwise the plain
  // "you have not yet reached the minimum" warning is already accurate.
  showBeforeDiscountsExplanation: Signal<boolean> = computed(
    () => this.minimumSpendValidationMethod() === MINIMUM_SPEND_VALIDATION_METHOD.before_discounts
      && this.hasPricelistDiscount()
  );

  orderCouponsOriginalPrice: Signal<number> = computed(() => this.#getCouponsOriginalPrice());

  orderGrossSubTotal: Signal<number> = computed(() => this.#getGrossSubTotal());

  totalSavings: Signal<number> = computed(() => this.#getTotalSavings());

  // The Subtotal row renders qualifyingSubtotal(), so the savings only read as a
  // deduction when that subtotal is the pre-discount one. On after_discounts the
  // subtotal already has them baked in and the row is purely informational —
  // driven by the backend method so a backoffice switch flips both rows together.
  savingsRowAmount: Signal<number> = computed(() =>
    this.minimumSpendValidationMethod() === MINIMUM_SPEND_VALIDATION_METHOD.before_discounts
      ? -this.totalSavings()
      : this.totalSavings()
  );

  //#region membership savings (DEV-10551)
  hasMembershipFlow = signal(environment.config.flows.membership);
  membershipInfo = computed(() => this.odooOrder()?.paymentDetails?.membership || null);
  membershipSavingsOnOrder = computed(() => this.membershipInfo()?.savingsOnOrder || 0);
  membershipSavingsBreakdown = computed(() => this.membershipInfo()?.savingsBreakdown || null);
  membershipUpgradePreview = computed(() => this.membershipInfo()?.upgradePreview || null);
  hasMembership = computed(() => !!this.signalsStoreService.sessionSignal()?.settings?.hasMembership);

  /** Explorer flow (DEV-11262). Their cart is a cart, not an order on its way:
   *  nothing is placed and nothing is billed until the signup is finished. */
  isExplorer = computed(() => this.signalsStoreService.isExplorer());
  isAnonymousExplorer = computed(() => this.signalsStoreService.isAnonymousExplorer());

  hasCartProducts = computed(() => !!(
    this.groupedProducts()?.subscription.length ||
    this.odooOrderProducts()?.subscription.length ||
    this.groupedProducts()?.common.length ||
    this.odooOrderProducts()?.common.length
  ));

  /**
   * The explorer's cart is finished; what is missing is the account, the card,
   * or both. This is the friendly half of `EXPLORER_RESTRICTED` — the backend
   * refuses the checkout either way, so it is better asked for here than
   * discovered as an error.
   *
   * Both states land on /signup: an anonymous explorer starts at the beginning,
   * and an activated one is already sent straight to the payment step by the
   * stepper's own `hasSession && !hasPaymentMethod` rule. Only the wording
   * differs, because what they still owe us differs.
   */
  handleExplorerCheckout() {
    const needsAccount = this.isAnonymousExplorer();

    this.#modalContentService.openModal(ModalContentTypes.CONFIRMATION, {
      title: needsAccount ? 'Complete Your Signup' : 'Add a Payment Method',
      textContent: needsAccount
        ? `
          Your items are saved. Finish creating your account and add a payment method to
          place this order &mdash; nothing is charged until you do.
        `
        : `
          Your items are saved. Add a payment method to place this order &mdash; nothing is
          charged until your delivery.
        `,
      confirmButtonText: needsAccount ? 'Complete Signup' : 'Add Payment Method',
      cancelButtonText: 'Keep Shopping'
    }).closed.subscribe((result: { confirm: boolean } | null) => {
      if (!result?.confirm) return;
      this.signalsStoreService.showCartPreview.set(false);
      this.#router.navigate(['/signup']);
    });
  }

  upgradeMembership() {
    const plan = this.membershipUpgradePreview()?.membership;
    if (!plan) return;
    this.#membershipService.openUpgradeFlow(plan);
  }
  //#endregion

  orderCoupons: Signal<number> = computed(() => this.#calculationsService.getOrderCoupons(this.odooOrder()?.paymentDetails?.coupons || []));

  orderDeliveryFee = computed(() => this.#calculationsService.getOrderDeliveryFee(this.odooOrder(), this.qualifyingSubtotal(), this.products()));

  additionalFees: Signal<Fee[]> = computed(() => this.#calculationsService.calculateOrderAdditionalFees(this.odooOrder(), this.orderSubTotal()));

  additionalFeesTotal: Signal<number> = computed(() => this.#calculationsService.calculateOrderAdditionalFeesTotal(this.additionalFees()));

  hiddenCarouselProducts: Signal<number[]> = computed(() => this.signalsStoreService.hiddenCarouselProducts());

  orderTaxes = computed(() => this.#getOrderTaxes());

  orderCredits = computed(() => this.#getOrderCredits());

  appliedCredits: Signal<number> = computed(() => this.#getAppliedCredits());

  creditsBalance: Signal<number> = computed(() => this.#getCreditsBalance());

  orderTipAmount = computed(() => this.#getOrderTip());

  orderTotal = computed(() => this.#getTotalOrder());

  isTemporaryRouteChange = signal(false);

  temporaryRouteChangeMessage = signal('');

  orderDonationAmountVoluntary = computed(() => this.#getOrderDonationVoluntaryAmount());

  percentMinSpendForFreeDelivery: Signal<number> = computed(() => this.#calculatePercentForFreeDelivery());

  minSpendForFreeDelivery: Signal<number> = computed(() => this.odooOrder()?.paymentDetails?.deliveryFee?.minSpend || 0);

  updateDonation = ModalContentTypes.UPDATE_DONATION;

  createDonation = ModalContentTypes.CREATE_DONATION;

  updateTip = ModalContentTypes.UPDATE_TIP;

  createTip = ModalContentTypes.CREATE_TIP;

  isOpenModal = signal(false);

  addressInfo: Signal<any | null> = computed(() => this.#setUpAddressInfo())

  paymentMethodInfo: Signal<any | null> = computed(() => this.#setUpPaymentMethod())

  isDeliveryDetailsCollapsed = signal(true);

  areSkippedProductsCollapsed = signal(false);

  isAllowedOrder: Signal<boolean> = computed(() => this.hasMinOrder());

  hasPendingChanges: Signal<boolean> = computed(() => this.#checkPendingChanges());

  hasAutoApplicableCoupon: Signal<boolean> = computed(() => this.#checkAutoApplicableCoupon());

  coupon: WritableSignal<string> = signal(''); // actionTrigger

  showCouponErrorMessage: Signal<boolean> = computed(() => this.orderService.showCouponErrorMessage());

  couponErrorMessage: Signal<string> = computed(() => this.orderService.couponErrorMessage());

  skippedSubscriptions: Signal<any | null> = computed(() => this.#setUpSkippedSubscriptions());

  isEmptyOrder: Signal<boolean> = computed(() => {
    return !this.groupedProducts()?.subscription.length &&
      !this.odooOrderProducts()?.subscription.length &&
      !this.groupedProducts()?.common.length &&
      !this.odooOrderProducts()?.common.length
  })

  deliveryDetailsText = computed(() => this.#setUpDeliveryDetailsText())

  isSkipped = computed(() => !!this.odooOrder()?.isSkipped);

  isPickup = computed(() => this.signalsStoreService.isAddressRequired() === false);

  isUpdating = signal(false);

  orderOnWay: Signal<{ id: number, deliveryDate: string, isOnWay: boolean } | null> = computed(() => this.#setUpOrderOnWay());

  /**
   * This property is isued to validate if the users can submit thier order despite not having reached the minimum price to save the order
   * because the bundle has not been published (its price is equal to zero).
   */
  orderHasUnpublishedBundle: Signal<boolean> = computed(() => {
    const products = this.products();
    const existsUnpublishedSubscriptionBundle = products?.subscription.some((p: any) => p?.bundle?.items && p.totalPrice === 0) || false;
    const existsUnpublishedAlaCarteBundle = products?.common.some((p: any) => p?.bundle?.items && p.totalPrice === 0) || false;
    return existsUnpublishedAlaCarteBundle || existsUnpublishedSubscriptionBundle;
  });

  marketStatus = computed(() => this.signalsStoreService.marketStatus());
  closedMarket = computed(() => {
    const ms = this.marketStatus();
    return {
      title: `Our Online Market Opens ${ms?.openingDay?.toUpperCase() || ''} Morning!`,
      legend: `We're busy sourcing next week's availability! Check back ${ms?.openingDay || ''} at ${ms?.openingHour || ''} to begin shopping.`
    };
  })

  //#region Preorder Products
  contactEmail = signal('');

  preOrdersSkipDisclaimer = computed(() => {
    const ce = this.contactEmail();
    if (!ce) return '';
    return `You cannot skip this week’s order as it contains pre-ordered products. For assistance, please contact us at ${ce}.`
  })

  preOrderedProductDisclaimer = signal('This pre-ordered product cannot be modified or removed.')
  hasPreorderedProducts = computed(() => {
    const odooOrder = this.odooOrder();
    return (odooOrder?.products?.common || []).some((p: { isPreOrder: boolean; }) => p.isPreOrder);
  });

  permissionsSetup = computed(() => this.#validatePermissions());

  //#endregion

  //#endregion

  constructor() {
    afterNextRender(() => {
      this.#validateTemporaryRoute();
      this.contactEmail.set(environment.config.contactEmail);
    });
  }

  ngOnInit(): void {
    this.orderService.getOrder();
  }

  setUpOdooProducts() {
    const odooOrder = this.odooOrder();
    const firebaseOrder = this.firebaseOrder();
    if (!odooOrder || !odooOrder?.products)
      return null

    if (!odooOrder?.products.common?.length && !odooOrder?.products.subscription?.length)
      return null

    let odooCommonProducts: any[] = odooOrder?.products.common;
    let firebaseCommonProducts: any[] = firebaseOrder?.products?.common || [];
    if (odooCommonProducts.length && firebaseCommonProducts.length) {
      const firebaseCommonProductsVariantIds = firebaseCommonProducts.map(e => e.variantId ?? e.variant.variantId);
      odooCommonProducts = odooCommonProducts.filter(p => !firebaseCommonProductsVariantIds.includes(p.variantId))
    }

    let odooSubscriptionProducts: any[] = odooOrder?.products.subscription;
    let firebaseSubscriptionProducts: any[] = firebaseOrder?.products?.subscription || [];
    if (odooSubscriptionProducts.length && firebaseSubscriptionProducts.length) {
      const firebaseSubscriptionProductsVariantIds = firebaseSubscriptionProducts.map(e => e.variantId ?? e.variant.variantId);
      odooSubscriptionProducts = odooSubscriptionProducts.filter(p => !firebaseSubscriptionProductsVariantIds.includes(p.variantId))
    }

    return { common: (odooCommonProducts || []).sort((a: any, b: any) => (b.updatedAt || 0) - (a.updatedAt || 0)), subscription: (odooSubscriptionProducts || []).sort((a: any, b: any) => (b.updatedAt || 0) - (a.updatedAt || 0)) };
  }

  groupProducts() {
    const firebaseOrder = this.firebaseOrder();

    if (!firebaseOrder || !firebaseOrder?.products)
      return null

    if (!firebaseOrder?.products.common?.length && !firebaseOrder?.products.subscription?.length)
      return null

    const common = (firebaseOrder?.products?.common || []).sort((a: any, b: any) => (b.updatedAt || 0) - (a.updatedAt || 0))
    const subscription = (firebaseOrder?.products?.subscription || []).sort((a: any, b: any) => (b.updatedAt || 0) - (a.updatedAt || 0))

    return { common, subscription };
  }


  hideShowCartPreview() {
    this.showCartPreview.set(false);
  }


  private setUpProducts(): any {
    const odooOrder = this.odooOrder();
    const firebaseOrder = this.firebaseOrder();

    const { common: odooCommonProducts = [], subscription: odooSubscriptionProducts = [] } = odooOrder?.products || {};
    const { common: fbCommonProducts = [], subscription: fbSubscriptionProducts = [] } = firebaseOrder?.products || {};

    const commonProducts = [...fbCommonProducts, ...odooCommonProducts];
    const subscriptionProducts = [...fbSubscriptionProducts, ...odooSubscriptionProducts]

    return {
      common: commonProducts,
      subscription: subscriptionProducts,
    }
  }

  private setUpCarouselItems(orderProductType: string, isOrderSuggestedProduct: boolean = true) {
    const hiddenProducts: number[] = this.hiddenCarouselProducts();
    if (this.odooOrder()?.isSkipped) return [];

    if (!this.isContentLoaded || !this.odooOrder()?.relatedProducts?.[orderProductType] || !this.odooOrder()?.relatedProducts?.[orderProductType]?.length)
      if (!this.odooOrder()?.relatedProducts?.[orderProductType] || !this.odooOrder()?.relatedProducts?.[orderProductType]?.length)
        return []

    let items: any = this.odooOrder()?.relatedProducts?.[orderProductType] || [];

    if (isOrderSuggestedProduct) {
      const allProductsId = [...this.products().common, ...this.products().subscription].map((product: any) => product.id)
      items = items.filter((product: any) => !allProductsId.includes(product.id));
    }

    if (hiddenProducts.length)
      items = items.filter((product: any) => hiddenProducts.indexOf(product.variantId) === -1)


    items = items.map((product: any) => {
      product.image = 'assets/images/logo.webp';
      product.name = product.name;
      if (typeof product?.price === 'number')
        product.price = product.price.toFixed(2);

      return ({
        content: {
          orderId: this.odooOrder()?.id,
          productDetails: { ...product, isSubscription: false },
          settings: { isCardInOrderPage: true, hideWhenWasOOS: true }
        },
      })
    })

    return items
  }

  //#region Methods

  #setUpDeliveryInfo() {
    if (!this.deliveriesService.deliveryZoneInfo()) return null

    const { deliveryDate, cutoffDate, cutoffTime, deliveryWindow } = (this.deliveriesService.deliveryZoneInfo()?.order || this.deliveriesService.deliveryZoneInfo()) as DeliveryInformation;
    const deliveryDateFormatted = formatDateToReadableString(deliveryDate)
    const cutoffDateFormatted = formatDateToReadableString(cutoffDate)

    const deliveryPickupDateText = this.signalsStoreService.isAddressRequired() ? 'Delivery Date: ' : 'Pick Up Date: ';

    const cutoffTimeFormatted = (cutoffTime || '').replace(/\./g, '').toUpperCase();
    return {
      deliveryWindow,
      deliveryDateText: `${this.odooOrder()?.isSkipped ? 'Next' : ''} ${deliveryPickupDateText}${deliveryDateFormatted.monthNumber}/${deliveryDateFormatted.dayNumber}/${deliveryDateFormatted.year}`,
      cutoffDateText: `${cutoffDateFormatted.dayName} ${cutoffDateFormatted.month} ${cutoffDateFormatted.day} at ${cutoffTimeFormatted}`,
      thanksMessage: this.signalsStoreService.isAddressRequired() ? 'Get excited for your delivery, which will arrive on:' : 'Your order will be ready for pick-up on:',
      mobileDeliveryDateText: `${deliveryDateFormatted.mobileDayName} ${deliveryDateFormatted.mobileFormat}`,
      mobileCoutoffText: `${cutoffDateFormatted.mobileDayName} ${cutoffDateFormatted.mobileFormat} ${cutoffTimeFormatted}`
    };
  }

  #calculatePercentForFreeDelivery() {
    const subtotal = this.qualifyingSubtotal();
    const minSpend = this.minSpendForFreeDelivery();
    return (subtotal * 100) / minSpend;
  }

  #getOrderTaxes() {
    const products = this.#calculationsService.setUpOrderProducts(this.odooOrder(), this.firebaseOrder());
    return this.#calculationsService.getOrderTaxes(products, this.odooOrder(), this.orderDeliveryFee());
  }

  #getTotalOrder() {
    const data = {
      subTotal: this.orderSubTotal(),
      deliveryFee: this.orderDeliveryFee(),
      taxes: this.orderTaxes(),
      tipAmount: this.orderTipAmount(),
      donationAmountVoluntary: this.orderDonationAmountVoluntary(),
      creditsAmount: this.orderCredits(),
      coupons: (this.orderCoupons() * -1),
      includeCoupons: !this.checkPendingChanges(),
      seasonalDeposits: this.odooOrder()?.paymentDetails?.seasonalDeposits || 0,
      additionalFees: this.additionalFeesTotal()
    };

    return this.#calculationsService.getOrderTotal(data);
  }

  #getOrderTip() {
    const products = this.#calculationsService.setUpOrderProducts(this.odooOrder(), this.firebaseOrder());
    return this.#calculationsService.getTipAmount(this.odooOrder(), this.firebaseOrder(), products);
  }

  #getOrderCredits() {
    return this.odooOrder()?.paymentDetails?.credits || 0;
  }

  #getOrderDonationVoluntaryAmount() {
    const products = this.#calculationsService.setUpOrderProducts(this.odooOrder(), this.firebaseOrder());
    return this.#calculationsService.getDonationVoluntaryAmount(this.odooOrder(), this.firebaseOrder(), products);
  }

  goToMarket() {
    this.#router.navigate(['/shop']);
    this.signalsStoreService.showCartPreview.set(false);
  }

  openPaymentMethodModal() {
    this.#modalContentService.openModal(ModalContentTypes.PAYMENT_METHOD, {
      title: 'Update payment method',
      closeable: true,
    });
  }

  openDeliveriesModal() {
    this.#modalContentService.openModal(ModalContentTypes.DELIVERIES, {
      title: 'Manage deliveries',
      closeable: true,
    });
  }

  openModalTip(action: string) {

    this.signalsStoreService.odooTipAmount.set(this.orderTipAmount() ?? 0);
    this.isOpenModal.set(true);

    this.#modalContentService
      .openModal(ModalContentTypes.TIP)
      .closed
      .subscribe((res) => {

        setTimeout(() => {
          this.isOpenModal.set(false);
        }, 500);

        if (!res?.amount) return;

        const payload = {
          amount: res?.amount,
          isRecurrent: res?.isRecurrent,
          applyToAllOrders: res?.applyToAllOrders
        }

        if (action === this.createTip) {
          this.#tipsDonationsService.create(payload, ModalContentTypes.TIP, false);
        } else {
          this.#tipsDonationsService.update(payload, ModalContentTypes.TIP, false);
        }
      })
  }

  deleteDonationVoluntary() {
    if (this.odooOrder()?.paymentDetails?.donation?.isRecurrent) {
      this.#modalContentService.openModal(ModalContentTypes.DELETE_TIP_AND_DONATION)
        .closed
        .subscribe((res) => {
          const applyToAllOrders = Number(res?.applyToAllOrders);
          this.#tipsDonationsService.delete(applyToAllOrders, ModalContentTypes.DONATION);
        })
    } else {
      this.#tipsDonationsService.delete(0, ModalContentTypes.DONATION);
    }
  }

  deleteTip() {
    this.isOpenModal.set(true);

    if (this.odooOrder()?.paymentDetails?.tip?.isRecurrent) {
      this.#modalContentService
        .openModal(ModalContentTypes.DELETE_TIP_AND_DONATION)
        .closed
        .subscribe((res) => {

          setTimeout(() => {
            this.isOpenModal.set(false);
          }, 500);

          const applyToAllOrders = Number(res?.applyToAllOrders);
          this.#tipsDonationsService.delete(applyToAllOrders, ModalContentTypes.TIP, false);
        });
    } else {
      this.#tipsDonationsService.delete(0, ModalContentTypes.TIP);
    }
  }

  checkPendingChanges() {
    return !!(this.products() &&
      [...this.products()?.common, ...this.products()?.subscription].some(product => product.hasPendingChanges));
  }

  //#region Order Info
  #setUpAddressInfo() {
    const session: Session = this.signalsStoreService.sessionSignal() as any;

    if (!session) return null;

    const { street, city, zip: zipCode, stateCode: state, additional } = session.address;
    const { location, name } = session?.pickUpInfo || {};

    const isAddressRequired = this.signalsStoreService.isAddressRequired();
    return {
      title: isAddressRequired ? 'Delivery Address' : 'Pick Up Address',
      text: isAddressRequired ? [street, city, state, zipCode].join(', ') : `${name} ${location ? `at ${location}` : ''}`,
      additional
    };
  }

  #setUpPaymentMethod() {
    const session: Session = this.signalsStoreService.sessionSignal() as any;

    if (!session) return null;

    return this.signalsStoreService.sessionSignal()?.creditCard?.number || '****'
  }

  #checkPendingChanges() {
    return !!(this.products() &&
      [...this.products()?.common, ...this.products()?.subscription].some(product => product.hasPendingChanges));
  }

  #checkAutoApplicableCoupon() {
    const odooOrder = this.odooOrder();
    if (!odooOrder?.paymentDetails?.coupons?.length) return false;
    const exists = odooOrder.paymentDetails.coupons.some((e: any) => e.autoApply);
    return exists;
  }
  #setUpSkippedSubscriptions() {

    const skippedSubscriptions = this.odooOrder()?.skippedSubscriptions || [];
    return skippedSubscriptions;
  }

  updateOrder(isFromButton = false) {
    const odooOrderId = this.odooOrder()?.id;

    if (isANoDeliveryDayUser(this.#localStorageService, this.#modalContentService))
      return
    if (isANoPaymentMethodUser(this.#localStorageService, this.#modalContentService, this.#activeModal))
      return
    if (isNoAddressUser(this.#localStorageService, this.#modalContentService, this.#activeModal, this.#router))
      return

    trackBeginCheckoutEvent(
      this.#dataLayerService,
      [...(this.products()?.subscription ?? []), ...(this.products()?.common ?? [])],
      this.orderTotal(),
      this.coupon(),
      this.orderTaxes(),
      this.orderDeliveryFee(),
      this.orderTipAmount()
    );

    let order = { ...this.firebaseOrder() };

    const orderData: any = {
      order,
      firebaseOrderSignal: this.signalsStoreService.firebaseOrder,
      getStock: false,
      showDefaultMessage: false,
      orderId: odooOrderId
    };

    if (isFromButton)
      orderData['deliveryInfo'] = this.deliveryInfo();

    this.orderService.updateOrder(orderData).pipe(tap(() => updateFirebaseOrderAndSubTrigger(this.signalsStoreService, this.#firebaseCrudService))).subscribe();
  }

  closeSideCart() {
    this.signalsStoreService.showCartPreview.set(false);
  }

  onChangeSkipWeekDelivery(unskip: boolean = false) {
    if (!unskip && !this.odooOrder()?.isSkipped) {
      if (environment.config.flows.order.askForDonationOnSkip)
        return this.openModalDonationSkippable();
      return this.openModalSkipWithDonationFlow();
    }

    const odooOrder = this.odooOrder();
    if (!odooOrder?.id) return

    const args = {
      orderId: +odooOrder.id,
      isSkipping: false,
      donationAmount: 0,
    }

    this.#skipOrUnskipOrder(args);
  }

  openModalDonationSkippable() {
    this.signalsStoreService.totalOrderAmount.set(this.odooOrder()?.paymentDetails?.subTotal || 0)
    this.#modalContentService.openModal(ModalContentTypes.DONATION_SKIPPABLE).closed
      .subscribe((res) => {
        if (!('isDonationCanceled' in res) || res?.isDonationCanceled) return;

        const odooOrder = this.odooOrder();
        if (!odooOrder?.id) return;
        const args = {
          orderId: +odooOrder.id,
          isSkipping: true,
          donationAmount: res?.donationAmount || 0,
        }
        this.#skipOrUnskipOrder(args);
      })
  }

  openModalSkipWithDonationFlow() {
    this.#modalContentService.openModal(ModalContentTypes.CONFIRMATION, {
      title: 'Are you sure?',
      textContent: `You are about to skip your delivery and you will not be receiving your order this week. Please note that any a la carte products will need to be re-added in future orders.`,
      cancelButtonText: 'Cancel',
      confirmButtonText: 'Confirm',
    }).closed
      .subscribe((res: { confirm: boolean } | null) => {
        if (!res?.confirm) return;
        const odooOrder = this.odooOrder();
        if (!odooOrder?.id) return;
        const args = {
          orderId: +odooOrder.id,
          isSkipping: true,
          donationAmount: 0,
        }
        this.#skipOrUnskipOrder(args);
      })
  }

  #skipOrUnskipOrder(args: any) {
    this.orderService.skipOrder(args).subscribe({
      next: () => {
        updateFirebaseOrderAndSubTrigger(this.signalsStoreService, this.#firebaseCrudService);
        this.#subscriptionsService.get();
      }
    });
  }


  #setUpDeliveryDetailsText() {
    const isAddressRequired = this.signalsStoreService.isAddressRequired();
    const isOrderSkipped = !!this.odooOrder()?.isSkipped;

    return isOrderSkipped ? `You've Skipped Your Order!` : isAddressRequired ? `Your Delivery Details` : `Your Pick Up Details`
  }

  #getCreditsBalance() {
    const creditsAmount = this.orderCredits();
    const subTotal = this.orderSubTotal();
    const deliveryFee = this.orderDeliveryFee();
    const taxes = this.orderTaxes();
    const tipAmount = this.orderTipAmount();
    const donationAmountVoluntary = this.orderDonationAmountVoluntary();

    return this.#calculationsService.getOrderCreditsBalance({
      creditsAmount,
      subTotal,
      deliveryFee,
      taxes,
      tipAmount,
      donationAmountVoluntary
    })
  }

  #getAppliedCredits() {
    const creditsAmount = this.orderCredits();
    const subTotal = this.orderSubTotal();
    const deliveryFee = this.orderDeliveryFee();
    const taxes = this.orderTaxes();
    const tipAmount = this.orderTipAmount();
    const donationAmountVoluntary = this.orderDonationAmountVoluntary();

    return this.#calculationsService.getOrderAppliedCredits({
      creditsAmount,
      subTotal,
      deliveryFee,
      taxes,
      tipAmount,
      donationAmountVoluntary
    })
  }

  applyCoupon() {
    if (this.isUpdating()) return;

    this.isUpdating.set(true);
    const odooOrder = this.odooOrder();
    if (odooOrder?.id)
      this.orderService.applyCouponWithOrder(this.coupon, +odooOrder.id)
        .pipe(
          finalize(() => this.isUpdating.set(false))
        ).subscribe();
    else {
      let data: any = {
        order: this.firebaseOrder(),
        firebaseOrderSignal: this.signalsStoreService.firebaseOrder,
        coupon: this.coupon,
      }

      if (this.deliveryInfo())
        data['deliveryInfo'] = this.deliveryInfo()

      this.orderService.updateOrder(data).pipe(
        finalize(() => this.isUpdating.set(false))
      ).subscribe();
    }
  }

  openModalDonation(action: string) {
    if (this.orderDonationAmountVoluntary())
      this.signalsStoreService.odooDonationAmountVoluntary.set(this.orderDonationAmountVoluntary());

    this.#modalContentService.openModal(ModalContentTypes.DONATION)
      .closed
      .subscribe((res) => {
        if (!res?.amount) return;

        const payload = {
          amount: res?.amount,
          isRecurrent: res?.isRecurrent,
          applyToAllOrders: res?.applyToAllOrders
        }

        if (action === ModalContentTypes.CREATE_DONATION) {
          this.#tipsDonationsService.create(payload, ModalContentTypes.DONATION);
        } else if (action === ModalContentTypes.UPDATE_DONATION) {
          this.#tipsDonationsService.update(payload, ModalContentTypes.DONATION);
        }
      })
  }

  #getCouponsOriginalPrice() {
    const coupons = this.odooOrder()?.paymentDetails?.coupons || [];
    return this.#calculationsService.getOrderCouponsOriginalPrice(coupons);
  }

  #getGrossSubTotal() {
    const odooOrder = this.odooOrder();
    const firebaseOrder = this.firebaseOrder();
    const couponsOriginalPrice = this.orderCouponsOriginalPrice();
    return this.#calculationsService.getOrderGrossSubTotal(odooOrder, firebaseOrder, couponsOriginalPrice);
  }

  #getTotalSavings() {
    return this.#calculationsService.getOrderTotalSavings({
      subtotal: this.orderSubTotal(),
      grossSubtotal: this.orderGrossSubTotal(),
    })
  }

  private hasMinOrder(): boolean {
    return this.qualifyingSubtotal() >= (this.odooOrder()?.paymentDetails?.minOrder || 0) || this.orderHasUnpublishedBundle();
  }

  #getOriginalPriceSubtotal() {
    const odooOrder = this.odooOrder();
    const firebaseOrder = this.firebaseOrder();
    const couponsOriginalPrice = this.orderCouponsOriginalPrice();
    return this.#calculationsService.getOriginalPriceSubtotal(odooOrder, firebaseOrder);
  }

  #validateTemporaryRoute() {

    const currentSessionInfo: Session | null = this.#localStorageService.get(LOCALSTORAGE_KEYS.SESSION);
    if (!currentSessionInfo)
      return;

    const { temporaryChange } = currentSessionInfo?.deliveryInfo ?? {};
    this.isTemporaryRouteChange.set(!!temporaryChange?.active);

    if (this.isTemporaryRouteChange())
      this.temporaryRouteChangeMessage.set(temporaryChange?.message ?? '');
  }

  #validatePermissions() {
    const permissions = this.signalsStoreService.permissions();
    const hasDeliveriesPermission = !!permissions?.settings.deliveries.allowed;
    const hasSubscripcionPermission = !!permissions?.settings.subscriptions.allowed;
    const hasPaymentMethodPermission = !!permissions?.settings.paymentMethod.allowed;

    return { hasDeliveriesPermission, hasSubscripcionPermission, hasPaymentMethodPermission };
  }

  //#endregion


  #setUpOrderOnWay() {
    // return {
    //   id: 1,
    //   deliveryDate: '06/17/2025',
    //   isOnWay: true
    // }
    const orderOnWay = this.odooOrder()?.activeOrderWeek || null;
    if (!orderOnWay) return null;

    return {
      id: orderOnWay.id,
      deliveryDate: formatDateToReadableString(orderOnWay.date).mmddyyyyFormat,
      isOnWay: orderOnWay.isOnWay || false
    }
  }


  //#endregion



}
